import { useCallback } from "react";
import { useLazyQuery } from "@apollo/client";

import {
  MEMBER_SEARCH,
  type MemberSearchInput,
  type MemberSearchResult,
  type MemberSearchVars,
} from "./graphql/memberSearch";
import {
  SEARCH_PROVIDER_PORTAL_MEMBERS,
  type ProviderPortalMember,
  type SearchProviderPortalMembersVars,
  type SearchProviderPortalMembersResponse,
} from "./graphql/providerPortalSearch";
import { useAuthContext } from "../auth/AuthContext";

interface QueryResponse { PathwaysMembersSearch: MemberSearchResult[] }

export interface UseMemberSearchReturn {
  search: (input: MemberSearchInput) => Promise<MemberSearchResult[]>;
  loading: boolean;
  error: Error | undefined;
  results: MemberSearchResult[];
  /**
   * Whether a name search must carry a Date of Birth — true for Provider
   * Portal logins, matching PP's own search rule. See memberSearchValidation.ts.
   */
  requiresDob: boolean;
}

// Exported for unit tests.
export function buildFilter(
  input: MemberSearchInput,
): MemberSearchVars["filter"] {
  // Send firstName/lastName as separate fields — PathwaysMembersSearch matches
  // names on that exact-match pair. The filter's single `name` field is NOT read
  // by the backend, so joining the two inputs into it produced a search with no
  // criteria and always came back empty. Keeping the fields separate is also
  // lossless: a backend-side split of a joined "Mary Jane Smith" cannot tell
  // which token belongs to which name.
  //
  // Both halves are sent together or not at all: the backend rejects a half pair
  // outright ("lastName is required when firstName is provided", BAD_USER_INPUT),
  // so emitting one alone would only ever produce an error. Callers that can hold
  // a partial name (the EHR auto-search) gate on having both.
  const first = input.firstName?.trim();
  const last = input.lastName?.trim();
  const hasFullName = !!first && !!last;
  const mpi = input.mpi?.trim();

  // Backend expects ISO date (YYYY-MM-DD); DatePicker holds MM/DD/YYYY.
  let dob: string | undefined;
  if (input.dob) {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(input.dob);
    dob = m ? `${m[3]}-${m[1]}-${m[2]}` : undefined;
  }

  return {
    ...(hasFullName ? { firstName: first, lastName: last } : {}),
    ...(dob ? { dob } : {}),
    ...(mpi ? { memberId: mpi } : {}),
    ...(input.showIneligibleMembers ? { showIneligibleMembers: true } : {}),
    isSearch: true,
  };
}

function buildIsoDate(dob: string | undefined): string | undefined {
  if (!dob) return undefined;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dob);
  return m ? `${m[3]}-${m[1]}-${m[2]}` : undefined;
}

function normalizePpMember(m: ProviderPortalMember): MemberSearchResult {
  return {
    // AM-5716 — carry BOTH opaque GUIDs in the route id, not just the mpi.
    // membMpiNo is non-nullable (Documents/Authorizations key off it and
    // must survive a hard refresh); membKeyId (mkey) is nullable and is the
    // resolution key @astrana/ui's usePatientProfile needs for the header
    // (`companyId && mkey`) — carried as a 4th, possibly-empty segment so a
    // hard refresh can rebuild `{companyId, mkey, mpi}` from the URL alone,
    // with no second search call. See MemberProfilePage's PP URL parsing.
    _id: `pp:${m.companyId}:${m.membMpiNo}:${m.membKeyId ?? ""}`,
    name:
      m.fullName?.trim() ||
      [m.firstName ?? "", m.lastName ?? ""].join(" ").trim() ||
      undefined,
    dob: m.dob ?? undefined,
    gender: m.gender ?? undefined,
    ipaCode: m.companyId,
    // PP's member search has no line-of-business field, so lineOfBusiness is
    // intentionally left undefined (the results row hides the LOB line for PP
    // logins). The only plan signal PP returns is the health plan name/code.
    healthplanName: m.healthPlanName ?? undefined,
    healthplanCode: m.healthPlanCode ?? undefined,
    memberIdOthers: { mpi: m.membMpiNo },
    memberId: m.membId ?? m.membMpiNo,
    memberPolicyId: m.membId ?? undefined,
    // PP-only fields the full profile needs to build its patient object
    // (mirrors providerPortalFrontend's Member360Panel). Carried via
    // location.state when navigating to /members/pp:<companyId>:<mpi>:<mkey>
    // (mkey also rides the URL itself as of AM-5716, but location.state
    // remains the primary, richer source — it also has membId/fullName).
    _pp: {
      companyId: m.companyId,
      membMpiNo: m.membMpiNo,
      membKeyId: m.membKeyId ?? null,
      membId: m.membId ?? null,
      fullName: m.fullName ?? null,
      startDate: m.startDate ?? null,
      // The member's HEALTH PLAN code (PP's RmiHpCode) — a DIFFERENT concept
      // from `companyId`/`ipaCode` (the IPA/tenant, e.g. "AAMG"). Create Claim
      // needs it as a REQUIRED, non-empty query param on
      // `ClaimMember/CreateClaim` (an empty string fails the .NET
      // `[Required]` binding exactly like a missing one → HTTP 400), and the
      // Create Claim entry gate's health-plan block rules key off it
      // (claims/claimMemberAccess.ts). It was already selected by the query
      // and surfaced in the results row, but was dropped here — so the full
      // profile never saw it. See toClaimContext.ts.
      healthPlanCode: m.healthPlanCode ?? null,
    },
  };
}

export function useMemberSearch(): UseMemberSearchReturn {
  const { selectedProvider } = useAuthContext();
  const isProviderPortal = selectedProvider === "provider-portal-b2c";

  // Pathways path
  const [executePathways, pathwaysQuery] = useLazyQuery<QueryResponse, MemberSearchVars>(
    MEMBER_SEARCH,
    { fetchPolicy: "network-only" },
  );

  // Provider Portal path
  const [executePP, ppQuery] = useLazyQuery<SearchProviderPortalMembersResponse, SearchProviderPortalMembersVars>(
    SEARCH_PROVIDER_PORTAL_MEMBERS,
    { fetchPolicy: "network-only" },
  );

  const search = useCallback(
    async (input: MemberSearchInput): Promise<MemberSearchResult[]> => {
      if (isProviderPortal) {
        const res = await executePP({
          variables: {
            input: {
              memberId: input.mpi?.trim() || undefined,
              firstName: input.firstName?.trim() || undefined,
              lastName: input.lastName?.trim() || undefined,
              dateOfBirth: buildIsoDate(input.dob),
            },
          },
        });
        // No Pathways fallback on an empty result: a PP login searches PP
        // only, the same as in providerPortalFrontend. PP needs a DOB with a
        // name, which the form enforces up front (validateMemberSearch).
        return (res.data?.searchProviderPortalMembers ?? []).map(normalizePpMember);
      }

      const res = await executePathways({
        variables: {
          pagination: { limit: 25, skip: 0 },
          filter: buildFilter(input),
        },
      });
      return res.data?.PathwaysMembersSearch ?? [];
    },
    [isProviderPortal, executePP, executePathways],
  );

  if (isProviderPortal) {
    const raw = ppQuery.data?.searchProviderPortalMembers ?? [];
    return {
      search,
      loading: ppQuery.loading,
      error: ppQuery.error ?? undefined,
      results: raw.map(normalizePpMember),
      requiresDob: true,
    };
  }

  return {
    search,
    loading: pathwaysQuery.loading,
    error: pathwaysQuery.error ?? undefined,
    results: pathwaysQuery.data?.PathwaysMembersSearch ?? [],
    requiresDob: false,
  };
}
